import React, { useEffect, useState } from 'react';
import AdminBookingFormAdapter from './AdminBookingFormAdapter';
import { useBookingStore } from '../../../store/bookingStore';

/**
 * Componente principal para o formulário administrativo de agendamentos
 * Fornece uma interface mobile-first para gerenciar agendamentos
 * com suporte a três modos: novo, remarcação e edição de serviços
 */
const AdminBookingForm = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  mode = 'new',
  services = [],
  professionals = [],
  salonId,
}) => {
  // Acesso ao store para resetar quando o modal é fechado
  const resetBooking = useBookingStore((state) => state.resetBooking);
  
  // Estado para controlar exibição do resumo
  const [bookingCompleted, setBookingCompleted] = useState(false);
  // Estado para armazenar dados do agendamento concluído
  const [completedBookingData, setCompletedBookingData] = useState(null);
  
  // Log de rastreamento para verificar como os dados iniciais são recebidos
  console.log('[AdminBookingForm] Recebido initialData:', {
    id: initialData?.id,
    servicos: initialData?.servicos,
    servicosType: initialData?.servicos ? typeof initialData.servicos : null,
    servicosIsArray: initialData?.servicos ? Array.isArray(initialData.servicos) : null,
    mode: mode
  });
  
  // Garantir que o campo servicos seja sempre um array
  if (initialData && initialData.servicos && !Array.isArray(initialData.servicos)) {
    console.log('[AdminBookingForm] Convertendo servicos para array:', initialData.servicos);
    initialData.servicos = Array.isArray(initialData.servicos) 
      ? initialData.servicos 
      : [initialData.servicos];
  }
  
  // Verificação final
  if (initialData?.servicos) {
    console.log('[AdminBookingForm] Servicos após verificação:', initialData.servicos);
  }
  
  // Resetar o estado global quando o modal é fechado
  useEffect(() => {
    if (!isOpen) {
      resetBooking();
      setBookingCompleted(false);
      setCompletedBookingData(null);
    }
  }, [isOpen, resetBooking]);
  
  // Não renderizar nada se o modal estiver fechado
  if (!isOpen) return null;
  
  // Callback para quando o formulário é finalizado com sucesso
  const handleFormComplete = (formData) => {
    // Armazenar dados do agendamento concluído
    setCompletedBookingData(formData);
    // Mostrar tela de resumo
    setBookingCompleted(true);
    // Enviar dados para o callback original
    onSubmit(formData);
  };
  
  // Manipulador para fechar manualmente o modal
  const handleClose = () => {
    setBookingCompleted(false);
    setCompletedBookingData(null);
    onClose();
  };
  
  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col h-full overflow-hidden">
      {bookingCompleted ? (
        // Resumo do agendamento concluído
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <h1 className="text-lg font-semibold">Agendamento Concluído</h1>
            <button 
              onClick={handleClose}
              className="text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-green-700 font-medium">Agendamento criado com sucesso!</p>
              </div>
            </div>
            
            {completedBookingData && (
              <div className="bg-white border rounded-md shadow-sm">
                <div className="border-b px-4 py-3">
                  <h2 className="font-semibold">Detalhes do Agendamento</h2>
                </div>
                
                <div className="p-4 space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">Cliente</p>
                    <p className="font-medium">{typeof completedBookingData.cliente === 'string' ? 
                      completedBookingData.cliente : completedBookingData.cliente?.nome}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Data e Horário</p>
                    <p className="font-medium">{completedBookingData.dataFormatada} às {completedBookingData.horario}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Serviços</p>
                    <ul className="list-disc list-inside">
                      {completedBookingData.servicosDetalhes?.map(servico => (
                        <li key={servico.id} className="font-medium">
                          {servico.nome} - R$ {servico.preco.toFixed(2)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Profissional</p>
                    <p className="font-medium">
                      {professionals.find(p => p.id === completedBookingData.profissional)?.nome || 
                       completedBookingData.profissional}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <div className="flex space-x-2 mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium 
                        ${completedBookingData.confirmado ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {completedBookingData.confirmado ? 'Confirmado' : 'Aguardando confirmação'}
                      </span>
                      {completedBookingData.pagamentoConfirmado && (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                          Pagamento confirmado
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Formulário de agendamento
        <AdminBookingFormAdapter
          isOpen={isOpen}
          onClose={onClose}
          onSubmit={handleFormComplete}
          initialData={initialData}
          mode={mode}
          services={services}
          professionals={professionals}
          salonId={salonId}
          isModal={true}
        />
      )}
    </div>
  );
};

export default AdminBookingForm; 